import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { useNavGuard } from "@/lib/navGuard";
import { DiscardConfirm } from "@/components/DiscardConfirm";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateTime";
import { CustomerNameFields } from "@/components/CustomerNameFields";
import { resolveCustomerNames } from "@/lib/customerName";
import {
  ArrowLeft,
  Save,
  AlertTriangle,
  User,
  Car,
  Calendar,
  Search,
  Clock,
} from "lucide-react";

// Time slots 10:00 AM – 4:00 PM, 30-min intervals
const TIME_SLOTS = [
  { value: "10:00", label: "10:00 AM", disabled: false },
  { value: "10:30", label: "10:30 AM", disabled: false },
  { value: "11:00", label: "11:00 AM", disabled: false },
  { value: "11:30", label: "11:30 AM", disabled: false },
  { value: "12:00", label: "12:00 PM", disabled: false },
  { value: "13:00", label: "1:00 PM", disabled: true },
  { value: "13:30", label: "1:30 PM", disabled: true },
  { value: "14:00", label: "2:00 PM", disabled: false },
  { value: "14:30", label: "2:30 PM", disabled: false },
  { value: "15:00", label: "3:00 PM", disabled: false },
  { value: "15:30", label: "3:30 PM", disabled: false },
  { value: "16:00", label: "4:00 PM", disabled: false },
];

const BASE_SERVICE_TYPES = ["1FS", "2FS", "3FS", "PMS", "RR", "BP", "Other"];
const PMS_INTERVALS = ["30K PMS", "40K PMS", "50K PMS", "60K PMS", "70K PMS", "80K PMS", "90K PMS", "100K PMS", "110K PMS", "120K PMS", "130K PMS", "140K PMS", "150K PMS", "160K PMS", "170K PMS", "180K PMS", "190K PMS", "200K PMS"];
const COUNTRY_CODES = [
  { code: "+91", country: "India", flag: "\u{1F1EE}\u{1F1F3}" },
  { code: "+977", country: "Nepal", flag: "\u{1F1F3}\u{1F1F5}" },
  { code: "+975", country: "Bhutan", flag: "\u{1F1E7}\u{1F1F9}" },
];

const NewAppointment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [selectedServiceBase, setSelectedServiceBase] = useState("");
  const [selectedPmsInterval, setSelectedPmsInterval] = useState("");
  const [editingDupId, setEditingDupId] = useState(null);
  const [newDupDate, setNewDupDate] = useState("");
  const [updatingDate, setUpdatingDate] = useState(false);

  // Local timezone dates
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const tmrw = new Date(now);
  tmrw.setDate(tmrw.getDate() + 1);
  const tomorrowStr = `${tmrw.getFullYear()}-${String(tmrw.getMonth() + 1).padStart(2, '0')}-${String(tmrw.getDate()).padStart(2, '0')}`;

  const [formData, setFormData] = useState({
    branch: "",
    appointment_date: tomorrowStr,
    appointment_time: "",
    source: "",
    salutation: "Mr",
    first_name: "",
    last_name: "",
    company_name: "",
    customer_phone: "",
    country_code: "+91",
    customer_email: "",
    vehicle_reg_no: "",
    vin: "",
    engine_number: "",
    model: "",
    current_km: "",
    ots_recall: false,
    service_type: "",
    allocated_sa: "",
    specific_repair_request: "",
    priority_customer: false,
    recovered_lost_customer: false,
  });

  // Helper used for both the reusable component AND existing handlers
  const updateField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  // Navigation guard — warn "Discard this entry?" when leaving with data entered.
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const navGuard = useNavGuard();
  const [pendingLeave, setPendingLeave] = useState(null);

  useEffect(() => {
    const initial = JSON.stringify(formDataRef.current);
    const isDirty = () => JSON.stringify(formDataRef.current) !== initial;
    navGuard?.setGuard(isDirty);
    const onBeforeUnload = (e) => {
      if (isDirty()) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      navGuard?.clearGuard();
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guardedNavigate = (to) => {
    if (navGuard?.isBlocked()) setPendingLeave(to);
    else navigate(to);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, branchesRes] = await Promise.all([
          axios.get(`${API}/settings`, { withCredentials: true }),
          axios.get(`${API}/branches`, { withCredentials: true }),
        ]);
        setSettings(settingsRes.data);
        // Filter branches: only Aftersales facility
        const aftersalesBranches = branchesRes.data.filter(
          (b) => b.type === "Aftersales"
        );
        setBranches(aftersalesBranches);
      } catch (error) {
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const checkDuplicates = async () => {
    // Per spec: count is per vehicle_reg_no only — drop phone matching here so
    // the visible panel and the block count stay consistent.
    if (!formData.vehicle_reg_no) {
      setDuplicates([]);
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append("vehicle", formData.vehicle_reg_no);
      const response = await axios.get(
        `${API}/appointments/duplicates/check?${params}`,
        { withCredentials: true }
      );
      setDuplicates(response.data);
    } catch (error) {
      console.error("Duplicate check failed:", error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.vehicle_reg_no.length >= 6) {
        checkDuplicates();
      } else {
        setDuplicates([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.vehicle_reg_no]);

  // Sync service type from base + PMS interval
  useEffect(() => {
    if (selectedServiceBase === "PMS" && selectedPmsInterval) {
      setFormData((prev) => ({ ...prev, service_type: selectedPmsInterval }));
    } else if (selectedServiceBase && selectedServiceBase !== "PMS") {
      setFormData((prev) => ({ ...prev, service_type: selectedServiceBase }));
      setSelectedPmsInterval("");
    }
  }, [selectedServiceBase, selectedPmsInterval]);

  const applyVehicleMatch = (match) => {
    setFormData((prev) => ({
      ...prev,
      vehicle_reg_no: match.vehicle_reg_no || prev.vehicle_reg_no,
      vin: match.vin || prev.vin,
      engine_number: match.engine_no || prev.engine_number,
      model: match.model || prev.model,
      salutation: match.salutation || prev.salutation,
      first_name: match.first_name || prev.first_name,
      last_name: match.last_name || prev.last_name,
      company_name: match.company_name || prev.company_name,
      customer_phone: match.customer_phone || prev.customer_phone,
      customer_email: match.customer_email || prev.customer_email,
    }));
  };

  const fetchCanonicalVehicle = async (regNo) => {
    if (!regNo) return null;
    try {
      const res = await axios.get(
        `${API}/reception/vehicle/${encodeURIComponent(regNo)}`,
        { withCredentials: true }
      );
      const d = res.data;
      if (!d) return null;
      return {
        source: "vehicle_db",
        vehicle_reg_no: d.vehicle_reg_no || "",
        vin: d.vin || "",
        engine_no: d.engine_no || "",
        model: d.model || "",
        salutation: d.salutation || (d.customer_type === "Company" ? "M/S" : "Mr"),
        company_name: d.company_name || "",
        customer_phone: d.contact_no || d.customer_phone || "",
        customer_email: d.customer_email || d.email || "",
        ...resolveCustomerNames(d),
      };
    } catch {
      return null;
    }
  };

  const lookupVehicle = async (q) => {
    const term = (q || "").trim();
    if (!term) {
      toast.info("Enter a value to search");
      return;
    }
    try {
      const res = await axios.get(
        `${API}/reception/search-vehicle?q=${encodeURIComponent(term)}`,
        { withCredentials: true }
      );
      const matches = Array.isArray(res.data) ? res.data : [];
      if (matches.length === 0) {
        toast.info("No linked data found.");
        return;
      }
      // Prefer vehicle_db source (canonical), fall back to first appointment hit
      const best = matches.find((m) => m.source === "vehicle_db") || matches[0];
      // The vehicles collection is the authoritative customer record — an
      // appointment row can still hold a previous owner's phone/email. Read it
      // directly and only fall back to the search hit when there is no record.
      const canonical = await fetchCanonicalVehicle(best.vehicle_reg_no);
      applyVehicleMatch(canonical || best);
      toast.success("Vehicle details filled");
    } catch {
      toast.info("No linked data found.");
    }
  };

  const handleSearchVin = () => lookupVehicle(formData.vin);
  const handleSearchReg = () => lookupVehicle(formData.vehicle_reg_no);

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "";
    return formatDate(dateStr);
  };

  const handleChangeDuplicateDate = async (appointmentId) => {
    if (!newDupDate) return;
    setUpdatingDate(true);
    try {
      await axios.put(
        `${API}/appointments/${appointmentId}`,
        { appointment_date: newDupDate },
        { withCredentials: true }
      );
      setDuplicates((prev) =>
        prev.map((d) =>
          d.appointment_id === appointmentId
            ? { ...d, appointment_date: newDupDate }
            : d
        )
      );
      setEditingDupId(null);
      setNewDupDate("");
      toast.success("Booking date updated");
    } catch (error) {
      toast.error("Failed to update date");
    } finally {
      setUpdatingDate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guard: backend also enforces this; bail early so we never show a server error toast.
    if (isBlocked) {
      toast.error("This vehicle already has 2 upcoming bookings. Please reschedule one before creating a new booking.");
      return;
    }
    const isCompany = formData.salutation === "M/S";
    const nameValid = isCompany
      ? !!formData.company_name?.trim()
      : (!!formData.first_name?.trim() && !!formData.last_name?.trim());
    if (!formData.vehicle_reg_no || !formData.model || !formData.current_km ||
        !formData.branch || !formData.appointment_time || !formData.source ||
        !formData.salutation || !nameValid || !formData.customer_phone || !formData.service_type) {
      toast.error("Please fill all required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        current_km: formData.current_km ? parseInt(formData.current_km) : null,
      };
      const response = await axios.post(`${API}/appointments`, payload, {
        withCredentials: true,
      });
      toast.success("Appointment created successfully");
      navGuard?.clearGuard();
      navigate(`/appointments/${response.data.appointment_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create appointment");
    } finally {
      setSaving(false);
    }
  };

  // Get display label for selected time
  const selectedTimeLabel = TIME_SLOTS.find((s) => s.value === formData.appointment_time)?.label || "";

  // Filter duplicates: only show today or future
  const futureDuplicates = duplicates.filter((d) => d.appointment_date >= todayStr);

  // Block creation when the same vehicle already has 2+ active upcoming bookings.
  // (Backend additionally enforces this; UI greys out the button and shows a badge.)
  const isBlocked = futureDuplicates.length >= 2;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-appointment-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="rounded-sm"
          onClick={() => guardedNavigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </Button>
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tighter uppercase">
            New Appointment
          </h1>
          <p className="text-sm text-gray-500 mt-1">Quick booking form</p>
        </div>
      </div>

      {/* Duplicate Warning */}
      {futureDuplicates.length > 0 && (
        <Card className="border-2 border-black rounded-sm shadow-none bg-gray-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-bold text-sm uppercase tracking-wider">
                  Potential Duplicate Found
                </span>
              </div>
              {isBlocked && (
                <span
                  className="flex items-center gap-1.5 text-red-600 font-bold text-xs uppercase tracking-wider"
                  data-testid="booking-blocked-badge"
                >
                  <AlertTriangle className="w-4 h-4" strokeWidth={2} />
                  Booking Blocked
                </span>
              )}
            </div>
            <div className="space-y-2">
              {futureDuplicates.map((d) => (
                <div
                  key={d.appointment_id}
                  className="p-3 bg-white border border-gray-200 rounded-sm"
                  data-testid={`duplicate-card-${d.appointment_id}`}
                >
                  <div
                    className="flex items-center justify-between cursor-pointer hover:opacity-75"
                    onClick={() => guardedNavigate(`/appointments/${d.appointment_id}`)}
                  >
                    <div>
                      <p className="text-sm font-medium">{d.customer_name}</p>
                      <p className="text-xs text-gray-500">
                        {d.customer_phone} {d.vehicle_reg_no && `\u2022 ${d.vehicle_reg_no}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-600" data-testid={`dup-date-${d.appointment_id}`}>
                        {formatDateDisplay(d.appointment_date)}
                      </span>
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:text-blue-800 underline font-medium"
                        data-testid={`change-date-btn-${d.appointment_id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingDupId(editingDupId === d.appointment_id ? null : d.appointment_id);
                          setNewDupDate(d.appointment_date);
                        }}
                      >
                        Change booking date
                      </button>
                    </div>
                  </div>
                  {editingDupId === d.appointment_id && (
                    <div
                      className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2"
                      data-testid={`date-picker-row-${d.appointment_id}`}
                    >
                      <Input
                        type="date"
                        value={newDupDate}
                        min={tomorrowStr}
                        onChange={(e) => setNewDupDate(e.target.value)}
                        className="rounded-sm h-8 text-xs w-44"
                        data-testid={`dup-date-input-${d.appointment_id}`}
                      />
                      <Button
                        type="button"
                        size="sm"
                        className="h-8 rounded-sm bg-black text-white hover:bg-gray-800 text-xs px-4"
                        disabled={updatingDate || !newDupDate}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChangeDuplicateDate(d.appointment_id);
                        }}
                        data-testid={`apply-date-btn-${d.appointment_id}`}
                      >
                        {updatingDate ? "..." : "Apply"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 rounded-sm text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingDupId(null);
                          setNewDupDate("");
                        }}
                        data-testid={`cancel-date-btn-${d.appointment_id}`}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        {/* 1. Vehicle Information */}
        <Card className="border border-gray-200 rounded-sm shadow-none mb-6">
          <CardHeader className="border-b border-gray-100 pb-4">
            <CardTitle className="font-heading font-bold text-lg tracking-tight uppercase flex items-center gap-2">
              <Car className="w-5 h-5" strokeWidth={1.5} />
              Vehicle Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <Label className="form-label">Registration No *</Label>
                <div className="relative">
                  <Input
                    value={formData.vehicle_reg_no}
                    onChange={(e) => setFormData({ ...formData, vehicle_reg_no: e.target.value.replace(/\s/g, "").toUpperCase() })}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = (e.clipboardData.getData("text") || "").replace(/\s/g, "").toUpperCase();
                      setFormData((prev) => ({ ...prev, vehicle_reg_no: pasted }));
                    }}
                    placeholder="Eg. WB74A1234"
                    className="rounded-sm pr-9"
                    data-testid="input-vehicle-reg"
                  />
                  <button
                    type="button"
                    onClick={handleSearchReg}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
                    data-testid="search-reg-btn"
                  >
                    <Search className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              <div>
                <Label className="form-label">Chassis Number</Label>
                <div className="relative">
                  <Input
                    value={formData.vin}
                    onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
                    placeholder="Chassis number"
                    className="rounded-sm pr-9"
                    data-testid="input-vin"
                  />
                  <button
                    type="button"
                    onClick={handleSearchVin}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
                    data-testid="search-vin-btn"
                  >
                    <Search className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              <div>
                <Label className="form-label">Engine Number</Label>
                <Input
                  value={formData.engine_number}
                  onChange={(e) => setFormData({ ...formData, engine_number: e.target.value.toUpperCase() })}
                  placeholder="Engine number"
                  className="rounded-sm"
                  data-testid="input-engine-number"
                />
              </div>

              <div>
                <Label className="form-label">Model *</Label>
                <Select
                  value={formData.model}
                  onValueChange={(v) => setFormData({ ...formData, model: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="input-model">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {settings?.vehicle_models?.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">Current KM *</Label>
                <Input
                  type="number"
                  value={formData.current_km}
                  onChange={(e) => setFormData({ ...formData, current_km: e.target.value })}
                  placeholder="Eg. 45000"
                  className="rounded-sm"
                  data-testid="input-km"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. Customer Information */}
        <Card className="border border-gray-200 rounded-sm shadow-none mb-6">
          <CardHeader className="border-b border-gray-100 pb-4">
            <CardTitle className="font-heading font-bold text-lg tracking-tight uppercase flex items-center gap-2">
              <User className="w-5 h-5" strokeWidth={1.5} />
              Customer Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              <CustomerNameFields formData={formData} updateField={updateField} />
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="form-label">Phone Number *</Label>
                  <div className="flex gap-1.5">
                    <Select
                      value={formData.country_code}
                      onValueChange={(v) => setFormData({ ...formData, country_code: v })}
                    >
                      <SelectTrigger className="rounded-sm w-[100px] shrink-0" data-testid="input-country-code">
                        <SelectValue>
                          {COUNTRY_CODES.find((c) => c.code === formData.country_code)?.flag}{" "}
                          {formData.country_code}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_CODES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.flag} {c.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      placeholder="10-digit mobile"
                      className="rounded-sm"
                      data-testid="input-customer-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label className="form-label">Email (Optional)</Label>
                  <Input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    placeholder="customer@email.com"
                    className="rounded-sm"
                    data-testid="input-customer-email"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3. Required Information */}
        <Card className="border border-gray-200 rounded-sm shadow-none mb-6">
          <CardHeader className="border-b border-gray-100 pb-4">
            <CardTitle className="font-heading font-bold text-lg tracking-tight uppercase flex items-center gap-2">
              <Calendar className="w-5 h-5" strokeWidth={1.5} />
              Required Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <Label className="form-label">Branch *</Label>
                <Select
                  value={formData.branch}
                  onValueChange={(v) => setFormData({ ...formData, branch: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="input-branch">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-400">
                        No Aftersales branches found
                      </div>
                    ) : (
                      branches.map((b) => (
                        <SelectItem key={b.branch_id} value={b.location}>
                          {b.location}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">Date *</Label>
                <Input
                  type="date"
                  value={formData.appointment_date}
                  min={tomorrowStr}
                  onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                  className="rounded-sm"
                  data-testid="input-date"
                />
              </div>

              <div>
                <Label className="form-label">Time Slot *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full rounded-sm justify-start font-normal text-sm h-10"
                      data-testid="input-time-slot"
                    >
                      <Clock className="w-4 h-4 mr-2 text-gray-400" strokeWidth={1.5} />
                      {selectedTimeLabel || <span className="text-gray-500">Select time slot</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-3 rounded-sm" align="start">
                    <div className="grid grid-cols-3 gap-1.5">
                      {TIME_SLOTS.map((slot) => (
                        <Button
                          key={slot.value}
                          type="button"
                          variant={formData.appointment_time === slot.value ? "default" : "outline"}
                          size="sm"
                          disabled={slot.disabled}
                          className={`rounded-sm text-xs h-8 ${
                            formData.appointment_time === slot.value
                              ? "bg-black text-white hover:bg-gray-800"
                              : slot.disabled
                              ? "bg-gray-100 text-gray-300 cursor-not-allowed border-gray-100"
                              : ""
                          }`}
                          onClick={() => setFormData({ ...formData, appointment_time: slot.value })}
                          data-testid={`slot-${slot.value}`}
                        >
                          {slot.label}
                        </Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="form-label">Source *</Label>
                <Select
                  value={formData.source}
                  onValueChange={(v) => setFormData({ ...formData, source: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="input-source">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {settings?.sources?.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">Service Type *</Label>
                <Select
                  value={selectedServiceBase}
                  onValueChange={(v) => setSelectedServiceBase(v)}
                >
                  <SelectTrigger className="rounded-sm" data-testid="input-service-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BASE_SERVICE_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* PMS Interval sub-dropdown */}
              {selectedServiceBase === "PMS" && (
                <div>
                  <Label className="form-label">PMS Interval *</Label>
                  <Select
                    value={selectedPmsInterval}
                    onValueChange={(v) => setSelectedPmsInterval(v)}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="input-pms-interval">
                      <SelectValue placeholder="Select interval" />
                    </SelectTrigger>
                    <SelectContent>
                      {PMS_INTERVALS.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="form-label">Service Advisor</Label>
                <Select
                  value={formData.allocated_sa}
                  onValueChange={(v) => setFormData({ ...formData, allocated_sa: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="input-sa">
                    <SelectValue placeholder="Assign SA" />
                  </SelectTrigger>
                  <SelectContent>
                    {settings?.service_advisors?.map((sa) => (
                      <SelectItem key={sa} value={sa}>{sa}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 4. Additional Details (no DocketReady) */}
        <Card className="border border-gray-200 rounded-sm shadow-none mb-6">
          <CardHeader className="border-b border-gray-100 pb-4">
            <CardTitle className="font-heading font-bold text-lg tracking-tight uppercase">
              Additional Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              <div>
                <Label className="form-label">Specific Repair Request</Label>
                <Textarea
                  value={formData.specific_repair_request}
                  onChange={(e) => setFormData({ ...formData, specific_repair_request: e.target.value })}
                  placeholder="Describe any specific issues or requests..."
                  className="rounded-sm min-h-[100px]"
                  data-testid="input-repair-request"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-sm">
                  <div>
                    <p className="text-sm font-medium">OTS/Recall</p>
                    <p className="text-xs text-gray-500">Renault recall check</p>
                  </div>
                  <Switch
                    checked={formData.ots_recall}
                    onCheckedChange={(v) => setFormData({ ...formData, ots_recall: v })}
                    data-testid="switch-ots-recall"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-sm">
                  <div>
                    <p className="text-sm font-medium">Priority</p>
                    <p className="text-xs text-gray-500">High priority customer</p>
                  </div>
                  <Switch
                    checked={formData.priority_customer}
                    onCheckedChange={(v) => setFormData({ ...formData, priority_customer: v })}
                    data-testid="switch-priority"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-sm">
                  <div>
                    <p className="text-sm font-medium">Recovered</p>
                    <p className="text-xs text-gray-500">From lost customer list</p>
                  </div>
                  <Switch
                    checked={formData.recovered_lost_customer}
                    onCheckedChange={(v) => setFormData({ ...formData, recovered_lost_customer: v })}
                    data-testid="switch-recovered"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            className="rounded-sm"
            onClick={() => navigate(-1)}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-black text-white hover:bg-gray-800 rounded-sm text-xs uppercase tracking-wider px-8 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={saving || isBlocked}
            data-testid="save-btn"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Create Appointment
              </>
            )}
          </Button>
        </div>
      </form>

      <DiscardConfirm
        open={pendingLeave !== null}
        onOpenChange={(open) => { if (!open) setPendingLeave(null); }}
        onDiscard={() => {
          const to = pendingLeave;
          setPendingLeave(null);
          navGuard?.clearGuard();
          navigate(to);
        }}
      />
    </div>
  );
};

export default NewAppointment;
